import {
  IncidentState,
  IncidentType,
  StatusPageLayoutPreset,
  StatusPageTheme,
  StatusPageTimeFormat,
  StatusPageVisibilityLevel,
  UptimeCalculationType,
} from "@/lib/types/statuspages.ts";

export interface PublicComponentStatusInfo {
  value: string;
  rank: number;
  display: string;
  color: string;
  is_operational: boolean;
}

export interface PublicStatusPageComponent {
  id: number;
  name: string;
  is_group: boolean;
  status: string;
  status_display: string;
  description: string | null;
  subcomponents: PublicStatusPageComponent[] | null;
  cached_response_time: number | null;
  response_time_data: [string | number, number][] | null;
  has_drill_down: boolean;
  // Associated check id — the drill-down identifier, matching the legacy component URL
  service_id: number | null;
  // ISO timestamp of the newest incident activity affecting this component; null when untouched
  last_updated_at: string | null;
}

export interface PublicStatusPageAffectedComponent {
  id: number;
  name: string;
  status: string;
  status_display: string;
}

export interface PublicStatusPageIncidentUpdate {
  id: number;
  updated_at: string;
  description_html: string;
  incident_state: IncidentState;
  incident_state_display: string | null;
}

export interface PublicStatusPageIncident {
  id: number;
  name: string;
  starts_at: string;
  ends_at: string | null;
  duration: number;
  incident_type: IncidentType;
  incident_state: string | null;
  include_in_global_metrics: boolean;
  updates: PublicStatusPageIncidentUpdate[];
  affected_components: PublicStatusPageAffectedComponent[];
  latest_update_incident_state: IncidentState | null;
  latest_update_description: string | null;
}

export interface PublicStatusPageMetric {
  id: number;
  name: string;
}

export interface PublicStatusPage {
  id: number;
  name: string;
  slug: string | null;
  description: string | null;
  description_html: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  company_website_url: string | null;
  contact_email: string | null;

  timezone: string;
  used_timezone: string;
  time_format: StatusPageTimeFormat;
  default_status_date_range: number;
  default_history_date_range: number;
  max_visible_days: number | null;
  max_visible_component_days: number | null;

  show_status_tab: boolean | null;
  show_history_tab: boolean;
  show_active_incidents: boolean;
  show_component_response_time: boolean;
  show_summary_metrics: boolean;
  show_past_incidents: boolean;
  show_component_history: boolean;
  show_history_snake: boolean;
  hide_empty_tabs_status: boolean;
  hide_empty_tabs_history: boolean;
  allow_drill_down: boolean;
  allow_pdf_report: boolean;
  allow_search_indexing: boolean;

  layout_preset: StatusPageLayoutPreset;
  show_component_bars: boolean;
  show_component_descriptions: boolean;
  show_overall_status: boolean;
  show_branding_footer: boolean;

  allow_subscriptions: boolean;
  allow_subscriptions_email: boolean;
  allow_subscriptions_rss: boolean;
  allow_subscriptions_slack: boolean;
  allow_subscriptions_sms: boolean;
  allow_subscriptions_webhook: boolean;
  slack_oauth_url: string | null;

  custom_header_bg_color_hex: string | null;
  custom_header_text_color_hex: string | null;
  custom_header_html_inspire: string | null;
  custom_footer_html_inspire: string | null;
  custom_css_inspire: string | null;

  google_analytics_code: string | null;
  has_v4_google_analytics_code: boolean;

  theme: StatusPageTheme;

  uptime_calculation_type: UptimeCalculationType;

  global_is_operational: boolean;
  has_components_under_maintenance_state: boolean;
  has_components_under_critical_state: boolean;

  components: PublicStatusPageComponent[];
  metrics: PublicStatusPageMetric[];
  active_incidents: PublicStatusPageIncident[];
  upcoming_maintenance: PublicStatusPageIncident[];
  component_status_metadata: PublicComponentStatusInfo[];
  visibility_level: StatusPageVisibilityLevel;
}

export interface PublicStatusPageDowntime {
  service_id: number;
  start_ts: number;
  end_ts: number | null;
}

export interface PublicStatusPageGlobalMetrics {
  uptime_pct: number | null;
  downtime_secs: number;
  outages: number;
}

export interface PublicStatusPageHistoryResult {
  past_incidents: PublicStatusPageIncident[];
  component_history: ComponentHistoryEntry[];
  downtime_history: PublicStatusPageDowntime[];
  uptime_calculation_type: string;
  global_metrics: PublicStatusPageGlobalMetrics;
}

export interface ComponentHistoryEntry {
  id: number;
  name: string;
  is_group: boolean;
  major_outage: number;
  partial_outage: number;
  degraded_performance: number;
  avg_response_time: number | null;
  uptime_pct: number | null;
  outages: number;
  downtime_secs: number;
  performance_data_type?: string | null;
  service_url?: string | null;
  service_uptime_sla?: number | null;
  service_id?: number | null;
  children?: ComponentHistoryEntry[];
}

export interface ComponentDrilldownResponseTimeLocation {
  location: string;
  data: [number, number][];
}

export interface ComponentDrilldownResult {
  component_id: number;
  component_name: string;
  check_type: string | null;
  check_type_display: string | null;
  address: string | null;
  current_status: string;
  current_status_display: string;
  is_up: boolean;
  uptime_pct: number | null;
  check_interval: number | null;
  downtime_secs: number | null;
  outages: number | null;
  last_downtime_at: string | null;
  alerts_count: number | null;
  last_alert_at: string | null;
  uptime_calculation_type: string;
  downtime_history: PublicStatusPageDowntime[];
  response_time_data: ComponentDrilldownResponseTimeLocation[];
  response_time_high: number | null;
  response_time_low: number | null;
  response_time_avg: number | null;
}

export enum TabParams {
  STATUS = "statusTab",
  HISTORY = "historyTab",
  TAB = "tab",
  EVENT = "event",
}

export enum StatusPageTab {
  STATUS = "status",
  HISTORY = "history",
}

export enum HistoryTabType {
  HISTORY_OVERVIEW = "history_overview",
  COMPONENT_HISTORY = "component_history",
  PAST_INCIDENTS = "past_incidents",
  PAST_MAINTENANCE = "past_maintenance",
}

export enum CurrentStatusTabType {
  OVERVIEW = "overview",
  ACTIVE_INCIDENTS = "active_incidents",
  ACTIVE_MAINTENANCE = "active_maintenance",
  SCHEDULED_MAINTENANCE = "scheduled_maintenance",
  COMPONENT_STATUS = "component_status",
  COMPONENT_HISTORY = "component_history",
}
